import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ExpenseStats, MemberIndexEntry, Party } from '../types/types';
import Layout from '../components/Layout';
import RoleTag from '../components/RoleTag';
import { hasCoalition, shortPartyName } from '../utils/party';
import { photoFor } from '../utils/names';
import { useBreakpoint } from '../useBreakpoint';
import useUrlParams from '../hooks/useUrlParams';
import { knessetFromParam, useScope } from '../hooks/useScope';
import KnessetMenu from '../components/KnessetMenu';
import CardLink from '../layout/CardLink';
import Grid from '../layout/Grid';
import PageContainer from '../layout/PageContainer';
import TwoPane from '../layout/TwoPane';
import {
  getAgendaMotionStats, getAllMembersOf, getAllVendors, getExpenseStats, getLobbyistsOf, getMemberSummaries, getPartiesOf, getScope, getVendor,
  VendorCrossRef, VendorSummary,
} from '../data/cache';
import { buttonReset, color, font, radius, shadow, space, TAP, text, weight } from '../theme';
import PageHeader from '../components/PageHeader';
import TabBar from '../components/TabBar';
import { Card, CardHeader, Section } from '../components/Card';
import { StatTile, StatTileRow } from '../components/StatTile';
import { Badge } from '../components/Badge';
import Bar, { SegmentBar } from '../components/Bar';
import ListRow from '../components/ListRow';
import Avatar from '../components/Avatar';
import Button from '../components/Button';
import Chip from '../components/Chip';
import MetricsCard, { MetricGroup } from '../components/MetricsCard';
import { ResultBar } from '../components/SelectMenu';
import Icon from '../components/Icon';
import { EmptyState, ErrorState } from '../components/States';
import { Skeleton, SkeletonList, SkeletonText, SkeletonTiles } from '../components/Skeleton';
import PresenceTab from './PresenceTab';
import PresenceLegend from '../components/PresenceLegend';
import { usePresenceNow } from '../hooks/usePresence';
import { isPresenceStale } from '../utils/presence';

/* ── Types ──────────────────────────────────────────────────────────────── */

type ActiveTab = 'parties' | 'members' | 'presence';
type Ordering = 'coalition' | 'seats' | 'wing';
// The MK tab's metrics card: the same groups as the party page (owner's option A)
type MkMetric = 'age' | 'seniority' | 'committees' | 'bills' | 'inProgress' | 'passed' | 'passRate' | 'votes' | 'queries' | 'agendaMotions' | 'expenses' | 'lobbyists';
type MkFilter = 'all' | 'women' | 'men' | 'ministers' | 'norwegian' | 'newMks' | 'veterans';

const MK_METRICS: MkMetric[] = ['age', 'seniority', 'committees', 'bills', 'inProgress', 'passed', 'passRate', 'votes', 'queries', 'agendaMotions', 'expenses', 'lobbyists'];

const MK_METRIC_GROUPS: MetricGroup<MkMetric>[] = [
  { key: 'profile', label: 'פרופיל', keys: ['age', 'seniority', 'committees'] },
  // 5c-B (as on the party page): the pass rate sits with the bills; activity gains the motions
  { key: 'legislation', label: 'חקיקה', keys: ['bills', 'inProgress', 'passed', 'passRate'] },
  { key: 'activity', label: 'פעילות', keys: ['votes', 'queries', 'agendaMotions'] },
  { key: 'transparency', label: 'שקיפות', keys: ['expenses', 'lobbyists'] },
];
const MK_METRIC_LABEL: Record<MkMetric, string> = {
  age: 'גיל ממוצע', seniority: 'ותק ממוצע', committees: 'חברויות בוועדות',
  bills: 'הגשות', inProgress: 'בתהליך', passed: 'חוקים שעברו',
  passRate: 'אחוז הצלחה', votes: 'נוכחות', queries: 'שאילתות', agendaMotions: 'הצעות לסדר',
  expenses: 'הוצאות ממוצע', lobbyists: 'שדלנים',
};
// The status line: "לפי נוכחות, מהגבוה לנמוך"
const MK_METRIC_PHRASE: Record<MkMetric, string> = {
  age: 'גיל', seniority: 'ותק', committees: 'מספר ועדות',
  bills: 'הגשות', inProgress: 'הצעות בתהליך', passed: 'חוקים שעברו',
  passRate: 'אחוז הצלחה', votes: 'נוכחות', queries: 'שאילתות', agendaMotions: 'הצעות לסדר היום',
  expenses: 'הוצאות', lobbyists: 'שדלנים',
};
const MK_FILTERS: { key: MkFilter; label: string; noun: string }[] = [
  { key: 'all', label: 'הכל', noun: 'חברי כנסת' },
  { key: 'women', label: 'נשים', noun: 'חברות כנסת' },
  { key: 'men', label: 'גברים', noun: 'חברי כנסת' },
  { key: 'ministers', label: 'שרים', noun: 'שרים' },
  { key: 'norwegian', label: 'נורווגיים', noun: 'שרים נורווגיים' },
  { key: 'newMks', label: 'חדשים', noun: 'ח״כים חדשים' },
  { key: 'veterans', label: 'ותיקים', noun: 'ותיקים (3 כנסות ומעלה)' },
];

// A member with its party resolved, as the directory shows it
interface MkRow extends MemberIndexEntry {
  age?: number;
  partyName: string;
  partyColor: string;
  isCoalition?: boolean;
}

const isWoman = (m: MkRow) => m.gender === 'נקבה' || (m.gender as string) === 'female';
const isMan = (m: MkRow) => m.gender === 'זכר' || (m.gender as string) === 'male';
const fmtShekels = (n: number) => '₪' + Math.round(n).toLocaleString('he-IL');
const fmtShort = (n: number) => (n >= 1000000 ? `₪${(n / 1000000).toFixed(1)}M` : `₪${Math.round(n / 1000)}K`);
const avgOf = (vals: number[]) => (vals.length > 0 ? Math.round(vals.reduce((s, v) => s + v, 0) / vals.length) : null);

const contentPadding = (isDesktop: boolean) => (isDesktop ? 0 : `${space.sm}px ${space.lg}px ${space.lg}px`);

/* ── Power balance ───────────────────────────────────────────────────────── */

// Coalition against opposition, then every party's seats; each segment links to its party
const PowerBalance = ({ coalition, opposition }: { coalition: Party[]; opposition: Party[] }) => {
  const coalitionSeats = coalition.reduce((s, p) => s + p.seats, 0);
  const oppositionSeats = opposition.reduce((s, p) => s + p.seats, 0);
  const total = coalitionSeats + oppositionSeats || 1;
  const status = coalitionSeats > 60 ? 'רוב לקואליציה' : coalitionSeats === 60 ? 'שוויון' : 'הקואליציה במיעוט';
  const segment = (p: Party, fallback: string) => (
    <Link
      key={p.id}
      to={`/parties/${p.id}`}
      state={{ from: 'הכנסת' }}
      title={`${p.name}: ${p.seats} מנדטים`}
      aria-label={`${p.name}, ${p.seats} מנדטים`}
      style={{ width: `${(p.seats / total) * 100}%`, background: p.color || fallback }}
    />
  );
  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', textAlign: 'center' }}>
        <div>
          <div style={{ fontSize: 30, fontWeight: weight.bold, color: color.coalition, lineHeight: 1.1 }}>{coalitionSeats}</div>
          <div style={{ fontSize: font.small, color: color.textSecondary }}>קואליציה</div>
        </div>
        <div>
          <div style={{ fontSize: font.heading, fontWeight: weight.bold, color: color.text }}>מאזן כוחות</div>
          <div style={{ fontSize: font.small, color: color.textSecondary }}>{status}</div>
        </div>
        <div>
          <div style={{ fontSize: 30, fontWeight: weight.bold, color: color.oppositionText, lineHeight: 1.1 }}>{oppositionSeats}</div>
          <div style={{ fontSize: font.small, color: color.textSecondary }}>אופוזיציה</div>
        </div>
      </div>
      <div style={{ marginTop: space.md }}>
        <SegmentBar
          height={10}
          label={`קואליציה ${coalitionSeats}, אופוזיציה ${oppositionSeats}`}
          segments={[{ key: 'c', value: coalitionSeats, color: color.coalition }, { key: 'o', value: oppositionSeats, color: color.opposition }]}
        />
      </div>
      {/* Per party: each segment is a link to the party */}
      <div style={{ display: 'flex', gap: 2, height: 14, marginTop: space.xs + 2, borderRadius: radius.sm, overflow: 'hidden' }}>
        {coalition.map(p => segment(p, color.coalition))}
        {opposition.map(p => segment(p, color.opposition))}
      </div>
      <div style={{ fontSize: font.caption, color: color.textTertiary, textAlign: 'center', marginTop: space.sm }}>כל פס מוביל לעמוד הסיעה</div>
    </Card>
  );
};

// Before a coalition exists: seats by political camp (the site's classification), so the
// balance of power still reads at a glance
const CampBalance = ({ groups }: { groups: { wing: string; parties: Party[] }[] }) => {
  const seatsOf = (ps: Party[]) => ps.reduce((s, p) => s + p.seats, 0);
  return (
    <Card>
      <div style={{ fontSize: font.heading, fontWeight: weight.bold, color: color.text }}>מושבים לפי מחנה</div>
      <div style={{ marginTop: space.md }}>
        <SegmentBar
          height={14}
          label={groups.map(g => `${g.wing} ${seatsOf(g.parties)}`).join(', ')}
          segments={groups.map(g => ({ key: g.wing, value: seatsOf(g.parties), color: WING_COLORS[g.wing] || color.textTertiary }))}
        />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.max(groups.length, 1)}, minmax(0, 1fr))`, gap: space.xs, textAlign: 'center', marginTop: space.md }}>
        {groups.map(g => (
          <div key={g.wing}>
            <div style={{ fontSize: font.heading, fontWeight: weight.bold, color: color.text }}>{seatsOf(g.parties)}</div>
            <div style={{ fontSize: font.caption, color: color.textSecondary }}>{g.wing}</div>
          </div>
        ))}
      </div>
      <div style={{ fontSize: font.caption, color: color.textTertiary, marginTop: space.md }}>המחנות לפי הסיווג באתר · קואליציה ואופוזיציה יוצגו כשתושבע הממשלה החדשה</div>
    </Card>
  );
};

/* ── Compare card ────────────────────────────────────────────────────────── */

type CompareKey = 'seats' | 'women' | 'ministers' | 'age' | 'seniority' | 'newMks' | 'attendance' | 'bills' | 'passed' | 'passRate' | 'queries' | 'agendaMotions' | 'committees' | 'expenses' | 'totalExpenses';

/* The 15 metrics in 5 groups of up to 3, and 4 in פעילות (with the motions for the agenda,
   2026-10-17), on a 2×2 grid: pick a group (segmented control), then a metric (equal-width
   chips). Every option stays visible, each step offers a handful of choices, and the other
   groups fill the same 3-slot row. Chip labels
   are short; the line under them says exactly what the bars show. */
type CompareGroup = 'composition' | 'tenure' | 'legislation' | 'activity' | 'expenses';

const COMPARE_GROUPS: { key: CompareGroup; label: string; keys: CompareKey[] }[] = [
  { key: 'composition', label: 'הרכב', keys: ['seats', 'women', 'ministers'] },
  { key: 'tenure', label: 'גיל וותק', keys: ['age', 'seniority', 'newMks'] },
  { key: 'legislation', label: 'חקיקה', keys: ['bills', 'passed', 'passRate'] },
  { key: 'activity', label: 'פעילות', keys: ['attendance', 'queries', 'agendaMotions', 'committees'] },
  { key: 'expenses', label: 'הוצאות', keys: ['expenses', 'totalExpenses'] },
];

const COMPARE_LABELS: Record<CompareKey, string> = {
  seats: 'מנדטים', women: 'נשים', ministers: 'שרים',
  age: 'גיל', seniority: 'ותק', newMks: 'חדשים',
  bills: 'הגשות', passed: 'עברו', passRate: 'הצלחה',
  attendance: 'נוכחות', queries: 'שאילתות', agendaMotions: 'הצעות לסדר', committees: 'ועדות',
  expenses: 'ממוצע', totalExpenses: 'סה״כ',
};

const COMPARE_HINTS: Record<CompareKey, string> = {
  seats: 'מספר המושבים של כל סיעה בכנסת',
  women: 'חברות כנסת בכל סיעה',
  ministers: 'שרים שמכהנים גם כחברי כנסת',
  age: 'הגיל הממוצע של חברי הסיעה',
  seniority: 'שנים ממוצעות מאז הכניסה הראשונה לכנסת',
  newMks: 'חברי כנסת בכנסת הראשונה שלהם',
  bills: 'הצעות חוק שחברי הסיעה בין שלושת היוזמים הראשונים שלהן',
  passed: 'הצעות חוק בהובלת חברי הסיעה שהפכו לחוק',
  passRate: 'אחוז ההצעות שעברו מתוך ההגשות',
  attendance: 'נוכחות ממוצעת בהצבעות המליאה',
  queries: 'שאילתות שהגישו חברי הסיעה',
  agendaMotions: 'הצעות לסדר היום שהגישו חברי הסיעה, לבד או כחלק מהצעה כוללת',
  committees: 'ועדות שבהן לסיעה חבר/ה מלא/ה',
  expenses: 'הוצאות ממוצעות לחבר/ת כנסת, כל השנים',
  totalExpenses: 'סך הוצאות חברי הסיעה, כל השנים',
};

const groupOf = (k: CompareKey): CompareGroup => (COMPARE_GROUPS.find(g => g.keys.indexOf(k) >= 0) || COMPARE_GROUPS[0]).key;

const ORDERING_TABS: { key: Ordering; label: string }[] = [
  { key: 'coalition', label: 'קואליציה' },
  { key: 'seats', label: 'מנדטים' },
  { key: 'wing', label: 'מחנה פוליטי' },
];
// Before a coalition exists: camp first, and no coalition view
const ORDERING_TABS_NO_COALITION: { key: Ordering; label: string }[] = [
  { key: 'wing', label: 'מחנה פוליטי' },
  { key: 'seats', label: 'מנדטים' },
];
const UNCLASSIFIED = 'ללא סיווג';

// Counts where 0 is a real answer, so parties with none still get a row
const SHOW_ZEROS: CompareKey[] = ['women', 'ministers', 'newMks', 'passed', 'passRate'];

const CompareCard = ({ parties, members }: { parties: Party[]; members: MkRow[] | null }) => {
  const [selected, setSelected] = useState<CompareKey>('seats');
  const group = COMPARE_GROUPS.find(g => g.key === groupOf(selected)) || COMPARE_GROUPS[0];

  const stats = useMemo(() => (members ? parties.filter(p => p.seats > 0).map(p => {
    const mems = members.filter(m => m.partyId === p.id);
    const led = mems.reduce((s, m) => s + (m.billStats ? m.billStats.led : 0), 0);
    const passed = mems.reduce((s, m) => s + (m.billStats ? m.billStats.passed : 0), 0);
    const committees: Record<string, boolean> = {};
    mems.forEach(m => (m.committeeIds || []).forEach(id => { committees[id] = true; }));
    const expenses = mems.map(m => m.expenseTotal || 0).filter(e => e > 0);
    return {
      party: p,
      women: mems.filter(isWoman).length,
      ministers: mems.filter(m => m.governmentMember).length,
      newMks: mems.filter(m => m.knessetCount === 1).length,
      age: avgOf(mems.map(m => m.age).filter((a): a is number => typeof a === 'number')),
      seniority: avgOf(mems.map(m => m.yearsInPolitics).filter((y): y is number => typeof y === 'number' && y > 0)),
      attendance: avgOf(mems.map(m => m.attendancePct).filter((a): a is number => typeof a === 'number')),
      led,
      passed,
      passRate: led > 0 ? Math.round((passed / led) * 100) : null,
      queries: mems.reduce((s, m) => s + (m.queryCount || 0), 0),
      agendaMotions: mems.reduce((s, m) => s + (m.agendaMotionCount || 0), 0),
      committees: Object.keys(committees).length,
      avgExpense: avgOf(expenses),
      totalExpense: expenses.reduce((s, e) => s + e, 0),
    };
  }) : []), [parties, members]);

  const rows = stats.map(s => {
    let val: number | null = null;
    let display = '';
    switch (selected) {
      case 'seats': val = s.party.seats; display = String(val); break;
      case 'women': val = s.women; display = String(val); break;
      case 'ministers': val = s.ministers; display = String(val); break;
      case 'newMks': val = s.newMks; display = String(val); break;
      case 'age': val = s.age; display = val != null ? String(val) : '—'; break;
      case 'seniority': val = s.seniority; display = val != null ? String(val) : '—'; break;
      case 'attendance': val = s.attendance; display = val != null ? `${val}%` : '—'; break;
      case 'bills': val = s.led; display = String(val); break;
      case 'passed': val = s.passed; display = String(val); break;
      case 'passRate': val = s.passRate; display = val != null ? `${val}%` : '—'; break;
      case 'queries': val = s.queries; display = val.toLocaleString('he-IL'); break;
      case 'agendaMotions': val = s.agendaMotions; display = val.toLocaleString('he-IL'); break;
      case 'committees': val = s.committees; display = String(val); break;
      case 'expenses': val = s.avgExpense; display = val ? fmtShort(val) : '—'; break;
      case 'totalExpenses': val = s.totalExpense; display = val ? fmtShort(val) : '—'; break;
    }
    return { party: s.party, val: val || 0, display };
  }).filter(r => SHOW_ZEROS.indexOf(selected) >= 0 || r.val > 0).sort((a, b) => b.val - a.val);
  const maxVal = rows.length > 0 ? Math.max(rows[0].val, 1) : 1;
  const knessetTotal = stats.reduce((s, x) => s + x.totalExpense, 0);

  return (
    <Card>
      <h2 style={{ ...text.heading, marginBottom: space.md }}>השוואת סיעות</h2>
      {/* Step 1: the group. Step 2: the metric, on an even 3-slot grid. */}
      <TabBar
        label="קבוצת מדדים"
        tabs={COMPARE_GROUPS.map(g => ({ key: g.key, label: g.label }))}
        active={group.key}
        onChange={k => setSelected((COMPARE_GROUPS.find(g => g.key === k) || COMPARE_GROUPS[0]).keys[0])}
      />
      <div role="group" aria-label={`מדדים: ${group.label}`} style={{ display: 'grid', gridTemplateColumns: `repeat(${group.keys.length === 4 ? 2 : 3}, minmax(0, 1fr))`, columnGap: space.xs + 2, marginTop: space.xs }}>
        {group.keys.map(k => <Chip key={k} variant="flat" block selected={selected === k} onClick={() => setSelected(k)}>{COMPARE_LABELS[k]}</Chip>)}
      </div>
      <div role="status" style={{ fontSize: font.small, color: color.textSecondary, lineHeight: 1.5, marginTop: space.xs }}>
        {COMPARE_HINTS[selected]}
        {selected === 'totalExpenses' && knessetTotal > 0 ? ` · בכנסת כולה: ${fmtShort(knessetTotal)}` : ''}
      </div>
      <div style={{ marginTop: space.md, borderTop: `1px solid ${color.divider}`, paddingTop: space.sm }}>
        {!members
          ? <div data-skeleton="true" style={{ display: 'flex', flexDirection: 'column', gap: space.sm }}><SkeletonText lines={5} /></div>
          : rows.map(r => (
            <Link
              key={r.party.id}
              to={`/parties/${r.party.id}`}
              state={{ from: 'הכנסת' }}
              style={{ display: 'flex', alignItems: 'center', gap: space.sm, minHeight: 36, color: color.text, textDecoration: 'none' }}
            >
              <span style={{ width: 4, height: 18, borderRadius: 2, background: r.party.color || color.border, flexShrink: 0 }} />
              <span style={{ width: 84, flexShrink: 0, fontSize: font.small, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.party.name}</span>
              <Bar value={r.val} max={maxVal} height={8} color={r.party.color || color.primary} style={{ flex: 1 }} />
              <span style={{ width: 52, flexShrink: 0, fontSize: font.small, fontWeight: weight.medium, color: color.textSecondary, textAlign: 'left' }}>{r.display}</span>
            </Link>
          ))}
      </div>
    </Card>
  );
};

/* ── Party cards ─────────────────────────────────────────────────────────── */

const PartyCard = ({ party }: { party: Party }) => (
  <CardLink
    to={`/parties/${party.id}`}
    state={{ from: 'הכנסת' }}
    style={{
      background: color.surface, borderRadius: radius.xl, boxShadow: shadow.card, padding: `${space.lg}px ${space.sm}px ${space.md}px`,
      display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', color: color.text, textDecoration: 'none', minWidth: 0,
    }}
  >
    <span style={{ width: 76, height: 68, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: space.sm }}>
      {party.logoUrl
        ? <img src={party.logoUrl} alt="" loading="lazy" decoding="async" width={76} height={68}
            style={{ width: '100%', height: '100%', objectFit: 'contain', transform: party.logoScale ? `scale(${party.logoScale})` : undefined }}
            onError={e => { (e.target as HTMLImageElement).style.visibility = 'hidden'; }} />
        : <Avatar name={party.name} size={60} shape="rounded" />}
    </span>
    <span style={{ fontSize: 24, fontWeight: weight.bold, color: color.primary, lineHeight: 1 }}>{party.seats}</span>
    <span style={{ fontSize: font.caption, color: color.textSecondary, marginTop: 2 }}>מנדטים</span>
    <span style={{ fontSize: font.small, fontWeight: weight.medium, marginTop: space.xs + 2, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{party.name}</span>
  </CardLink>
);

const PartyGrid = ({ parties }: { parties: Party[] }) => {
  const { isDesktop, isTablet } = useBreakpoint();
  // Desktop: as many 112px+ columns as fit beside the compare column; phone 3, tablet 4
  const style: React.CSSProperties = isDesktop
    ? { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(112px, 1fr))', gap: space.sm }
    : { display: 'grid', gridTemplateColumns: `repeat(${isTablet ? 4 : 3}, minmax(0, 1fr))`, gap: space.sm };
  return <div style={style}>{parties.map(p => <PartyCard key={p.id} party={p} />)}</div>;
};

const PartyBlock = ({ title, seats, swatch, parties }: { title: string; seats: number; swatch: string; parties: Party[] }) => (
  <Section
    title={<span style={{ display: 'inline-flex', alignItems: 'center', gap: space.sm }}><span style={{ width: 10, height: 10, borderRadius: 3, background: swatch }} />{title}</span>}
    meta={`${seats} מנדטים`}
  >
    <PartyGrid parties={parties} />
  </Section>
);

const WING_ORDER = ['ימין קיצוני', 'ימין', 'חרדי', 'מרכז', 'שמאל', 'ערבי'];
const WING_COLORS: Record<string, string> = {
  'ימין קיצוני': '#8B0000', 'ימין': '#E24B4A', 'חרדי': '#333333', 'מרכז': '#888888', 'שמאל': '#185FA5', 'ערבי': '#27500A',
};

/* ── MK directory ────────────────────────────────────────────────────────── */

const statValue = (m: MkRow, sort: MkMetric): number | null => {
  const b = m.billStats;
  switch (sort) {
    case 'bills': return b ? b.led : null;
    case 'inProgress': return b ? b.inProgress : null;
    case 'passed': return b ? b.passed : null;
    // As on the party page: only MKs who led 3 bills or more
    case 'passRate': return b && b.led >= 3 ? Math.round((b.passed / b.led) * 100) : null;
    case 'queries': return m.queryCount != null ? m.queryCount : null;
    case 'agendaMotions': return m.agendaMotionCount != null ? m.agendaMotionCount : null;
    case 'votes': return typeof m.attendancePct === 'number' ? m.attendancePct : null;
    case 'age': return m.age != null ? m.age : null;
    case 'lobbyists': return m.lobbyistCount != null ? m.lobbyistCount : null;
    case 'committees': return m.committeeIds ? m.committeeIds.length : null;
    case 'seniority': return m.yearsInPolitics != null ? m.yearsInPolitics : null;
    case 'expenses': return m.expenseTotal != null ? m.expenseTotal : null;
    default: return null;
  }
};

const statLabel = (v: number, sort: MkMetric): string => {
  if (sort === 'votes' || sort === 'passRate') return `${Math.round(v)}%`;
  if (sort === 'expenses') return `₪${Math.round(v / 1000)}K`;
  if (sort === 'age') return `בן/ת ${v}`;
  if (sort === 'seniority') return `${v} שנים`;
  if (sort === 'committees') return `${v} ועדות`;
  return v.toLocaleString('he-IL');
};

interface MkListRowProps {
  member: MkRow;
  sort: MkMetric | null;
  maxVal: number | null;
  filter: MkFilter;
  // Flat (sorted) lists show each MK's party; the party groups don't need it
  showParty: boolean;
  first: boolean;
  inGrid: boolean;
  // In the Knesset building now (a dot on the photo)
  present: boolean;
}

const MkListRow = ({ member, sort, maxVal, filter, showParty, first, inGrid, present }: MkListRowProps) => {
  const primaryRole = (member.roles || []).find(r => r.isPrimary && r.title !== 'חבר/ת כנסת' && r.title !== 'חבר כנסת');
  const val = sort ? statValue(member, sort) : null;
  // The MK page opens on the tab for the metric the list is sorted by
  const stat = filter === 'ministers' || filter === 'norwegian' ? undefined : sort === 'votes' ? 'attendance' : sort || undefined;
  return (
    <ListRow
      divider={!first && !inGrid}
      to={`/members/${member.id}`}
      state={{ from: 'חברי כנסת', stat }}
      leading={<Avatar name={member.name} src={photoFor(member, 40)} size={40} present={present} />}
      title={
        <span style={{ display: 'flex', alignItems: 'center', gap: space.sm, flexWrap: 'wrap' }}>
          {member.name}
          {member.isActiveMk === false && <span title="ויתר/ה על המושב בכנסת לפי החוק הנורווגי"><Badge>נורווגי</Badge></span>}
        </span>
      }
      subtitle={
        <span style={{ display: 'flex', flexDirection: 'column', gap: space.xs, marginTop: space.xxs }}>
          {(primaryRole || (showParty && member.partyName)) && (
            <span style={{ display: 'flex', alignItems: 'center', gap: space.xs, flexWrap: 'wrap' }}>
              {primaryRole && <RoleTag role={primaryRole} size="small" />}
              {showParty && member.partyName && <Badge dot={member.partyColor}>{member.partyName}</Badge>}
            </span>
          )}
          {sort && val != null && maxVal != null && (
            <span style={{ display: 'flex', alignItems: 'center', gap: space.sm }}>
              <Bar value={Math.max(val, 0)} max={maxVal} height={5} style={{ flex: 1 }} />
              <span style={{ fontSize: font.small, fontWeight: weight.bold, color: color.primary, flexShrink: 0 }}>{statLabel(val, sort)}</span>
            </span>
          )}
        </span>
      }
    />
  );
};

/* Knesset-wide expenses: the total, the top vendors (meta/expenseStats) and, per vendor,
   which MKs paid it (vendors/{id}). The whole vendor list loads only on "show all".
   Categories show once meta/expenseStats has byCategory (requested from the backend as B7). */
const VENDORS_PREVIEW = 10;

// The expense files (2023–2025) cover the 25th Knesset
const EXPENSES_KNESSET = 25;

const ExpenseBreakdown = ({ totalSpending }: { totalSpending: number }) => {
  const [stats, setStats] = useState<ExpenseStats | null | undefined>(undefined);
  const [view, setView] = useState<'vendors' | 'categories' | null>(null);
  const [all, setAll] = useState<VendorSummary[] | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [allError, setAllError] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [refs, setRefs] = useState<Record<string, VendorCrossRef | null>>({});
  const [names, setNames] = useState<Record<string, MemberIndexEntry>>({});
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getExpenseStats().then(s => { if (!cancelled) setStats(s); }).catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!showAll || all) return;
    let cancelled = false;
    setAllError(false);
    getAllVendors().then(v => { if (!cancelled) setAll(v); }).catch(() => { if (!cancelled) setAllError(true); });
    return () => { cancelled = true; };
  }, [showAll, all]);

  // The expanded vendor's MKs: from the full list when loaded, else its own doc; then their names
  const loadedRefs = useRef<Record<string, VendorCrossRef | null>>({});
  const expandedRef: VendorCrossRef | null | undefined = expanded
    ? (all ? all.find(v => v.name === expanded) || null : refs[expanded])
    : undefined;
  useEffect(() => {
    if (!expanded) return;
    let cancelled = false;
    const known = all
      ? Promise.resolve(all.find(v => v.name === expanded) || null)
      : Object.prototype.hasOwnProperty.call(loadedRefs.current, expanded)
        ? Promise.resolve(loadedRefs.current[expanded])
        : getVendor(expanded).catch(() => null);
    known.then(ref => {
      if (cancelled) return undefined;
      loadedRefs.current[expanded] = ref;
      setRefs({ ...loadedRefs.current });
      return ref ? getMemberSummaries(ref.mkIds).then(info => { if (!cancelled) setNames(prev => ({ ...prev, ...info })); }) : undefined;
    }).catch(() => { /* ok — IDs are shown instead of names */ });
    return () => { cancelled = true; };
  }, [expanded, all]);

  const top = stats && stats.topVendors ? stats.topVendors : [];
  const list: { name: string; total: number; mkCount: number }[] = showAll && all
    ? all.filter(v => v.totalSpending > 0).map(v => ({ name: v.name, total: v.totalSpending, mkCount: v.mkCount }))
    : top.map(v => ({ name: v.name, total: v.totalSpending, mkCount: v.mkCount }));
  const shown = showAll ? list : list.slice(0, VENDORS_PREVIEW);
  const maxVendor = list.length > 0 ? Math.max(list[0].total, 1) : 1;
  // Every MK with expenses this Knesset (incl. departed MKs and Norwegian ministers), the same
  // population as the vendor and category figures; the sitting MKs' sum until it loads
  const knessetTotal = (stats && stats.totalAllMks) || totalSpending;
  const vendorCount = stats ? stats.vendorCount || (all ? all.length : top.length) : null;
  const byCategory = stats && stats.byCategory ? stats.byCategory : null;
  const categories = byCategory ? Object.keys(byCategory).map(k => [k, byCategory[k]] as [string, number]).filter(e => e[1] > 0).sort((a, b) => b[1] - a[1]) : [];
  const categoryTotal = categories.reduce((s, e) => s + e[1], 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: space.md }}>
      <StatTileRow columns={categories.length > 0 ? 3 : 2}>
        <StatTile value={fmtShekels(knessetTotal)} label="סה״כ הוצאות הכנסת" title={stats && stats.mkCount ? `${stats.mkCount} חברי כנסת עם הוצאות בכנסת ה-${EXPENSES_KNESSET}` : undefined} />
        <StatTile
          value={vendorCount != null ? vendorCount.toLocaleString('he-IL') : '…'}
          label="ספקים"
          selected={view === 'vendors'}
          indicator="filter"
          onClick={top.length > 0 ? () => { setView(view === 'vendors' ? null : 'vendors'); setExpanded(null); } : undefined}
        />
        {categories.length > 0 && (
          <StatTile value={categories.length} label="קטגוריות" selected={view === 'categories'} indicator="filter" onClick={() => setView(view === 'categories' ? null : 'categories')} />
        )}
      </StatTileRow>
      {error && <Card padding={0}><ErrorState onRetry={() => { setError(false); setStats(undefined); getExpenseStats().then(setStats).catch(() => setError(true)); }} /></Card>}

      {view === 'categories' && categories.length > 0 && (
        <Card>
          <CardHeader title="לפי קטגוריה" meta="כל הכנסת · כל השנים" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: space.md }}>
            {categories.map(([cat, amount]) => (
              <div key={cat}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: space.sm, fontSize: font.small, marginBottom: space.xs }}>
                  <span style={{ fontWeight: weight.medium }}>{cat}</span>
                  <span style={{ color: color.textSecondary, flexShrink: 0 }}>{fmtShekels(amount)} ({categoryTotal > 0 ? Math.round((amount / categoryTotal) * 100) : 0}%)</span>
                </div>
                <Bar value={amount} max={categories[0][1]} height={6} />
              </div>
            ))}
          </div>
        </Card>
      )}

      {view === 'vendors' && (
        <Card padding={0}>
          <div style={{ padding: `${space.lg}px ${space.lg}px ${space.xs}px` }}>
            <CardHeader title="ספקים" meta={showAll && all ? `${list.length.toLocaleString('he-IL')} ספקים משותפים לכמה ח״כים או מעל ₪50K` : `${top.length} הגדולים · כל השנים`} style={{ marginBottom: 0 }} />
          </div>
          {shown.map((v, vi) => {
            const isOpen = expanded === v.name;
            const pct = knessetTotal > 0 ? Math.round((v.total / knessetTotal) * 100) : 0;
            const ref = isOpen ? expandedRef : undefined;
            const mkList = ref ? Object.keys(ref.byMk).map(id => ({ id, amount: ref.byMk[id] })).sort((a, b) => b.amount - a.amount) : [];
            const maxMk = mkList.length > 0 ? Math.max.apply(null, mkList.map(x => Math.abs(x.amount)).concat([1])) : 1;
            return (
              <div key={v.name} style={{ borderTop: vi > 0 ? `1px solid ${color.divider}` : 'none' }}>
                <button
                  type="button"
                  aria-expanded={isOpen}
                  onClick={() => setExpanded(isOpen ? null : v.name)}
                  style={{ ...buttonReset, width: '100%', boxSizing: 'border-box', padding: `${space.sm + 2}px ${space.lg}px`, display: 'flex', flexDirection: 'column', gap: space.xs + 2, minHeight: 48 }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: space.sm, width: '100%' }}>
                    <span style={{ color: color.textTertiary }}><Icon name={isOpen ? 'chevronUp' : 'chevronDown'} size={16} /></span>
                    <span style={{ flex: 1, minWidth: 0, fontSize: font.label, fontWeight: isOpen ? weight.bold : weight.medium, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.name}</span>
                    <span style={{ fontSize: font.small, color: color.textSecondary, flexShrink: 0 }}>{fmtShekels(v.total)} ({pct}%)</span>
                  </span>
                  {v.mkCount > 1 && <span style={{ display: 'flex' }}><Badge tone="info">{v.mkCount} ח״כים</Badge></span>}
                  <Bar value={v.total} max={maxVendor} height={6} style={{ width: '100%' }} />
                </button>
                {isOpen && (
                  <div style={{ margin: `0 ${space.lg}px ${space.md}px`, background: color.surfaceMuted, borderRadius: radius.md, padding: `${space.xs}px ${space.md}px` }}>
                    {ref === undefined && <div style={{ padding: `${space.sm}px 0` }}><SkeletonText lines={3} /></div>}
                    {ref === null && <div style={{ padding: `${space.sm}px 0`, fontSize: font.small, color: color.textSecondary }}>אין פירוט לפי חבר/ת כנסת לספק הזה.</div>}
                    {mkList.map((x, i) => (
                      <Link key={x.id} to={`/members/${x.id}`} state={{ from: 'חברי כנסת', stat: 'expenses' }}
                        style={{ display: 'block', padding: `${space.sm}px 0`, borderTop: i > 0 ? `1px solid ${color.border}` : 'none', color: color.text, textDecoration: 'none' }}>
                        <span style={{ display: 'flex', justifyContent: 'space-between', fontSize: font.small, marginBottom: space.xs }}>
                          <span style={{ color: color.primary, fontWeight: weight.medium }}>{names[x.id] ? names[x.id].name : x.id}</span>
                          <span style={{ color: color.textSecondary }}>{fmtShekels(x.amount)}</span>
                        </span>
                        <Bar value={Math.abs(x.amount)} max={maxMk} height={4} track={color.surface} />
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          <div style={{ padding: space.md, borderTop: `1px solid ${color.divider}` }}>
            {allError && <div style={{ marginBottom: space.sm }}><ErrorState onRetry={() => { setAllError(false); setAll(null); }} /></div>}
            {showAll && !all && !allError
              ? <SkeletonList rows={3} card={false} />
              : (
                <Button fullWidth onClick={() => { setShowAll(!showAll); setExpanded(null); }}>
                  {showAll ? `הצגת ${VENDORS_PREVIEW} הגדולים בלבד` : 'הצגת ספקים נוספים'}
                </Button>
              )}
          </div>
        </Card>
      )}
    </div>
  );
};

/* ── Members tab ─────────────────────────────────────────────────────────── */

/* The same pattern as the party page (owner's option A, components/MetricsCard): Knesset-
   wide figures on tiles in four groups, a tile sorts the list (tap again to flip), compact
   filter chips that combine with any sort, and a status line with a reset. Without a sort
   the list is grouped by bloc and party. */

interface MembersTabProps {
  members: MkRow[];
  norwegian: MkRow[];
  sort: MkMetric | null;
  dir: 'asc' | 'desc';
  filter: MkFilter;
  updateParams: (updates: Record<string, string | null>) => void;
  // Who is in the Knesset building now (fresh data only), for the dots on the photos
  present: Record<string, string>;
  // The Knesset shown (its lobbyists for the lobbyists tile)
  knesset: number;
}

const MembersTab = ({ members, norwegian, sort, dir, filter, updateParams, present, knesset }: MembersTabProps) => {
  const { isDesktop } = useBreakpoint();
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [lobbyistTotal, setLobbyistTotal] = useState<number | null>(null);
  const [motionTotal, setMotionTotal] = useState<number | null>(null);

  // The lobbyists tile counts every lobbyist who appeared before a committee of the Knesset shown
  useEffect(() => {
    let cancelled = false;
    getLobbyistsOf(knesset).then(ls => { if (!cancelled) setLobbyistTotal(ls.length); }).catch(() => { /* ok — the tile shows "—" */ });
    // The motions tile counts the Knesset's motions (a combined one once, though several MKs moved it)
    Promise.all([getAgendaMotionStats(), getScope()]).then(([st, sc]) => {
      if (!cancelled && st && (st.knessetNumber === undefined || st.knessetNumber === sc.knesset)) setMotionTotal(st.total);
    }).catch(() => { /* ok — the tile shows "—" */ });
    return () => { cancelled = true; };
  }, [knesset]);

  // A tile sorts high to low; the sorted tile flips the direction
  const pickSort = (k: MkMetric) => {
    if (sort === k) updateParams({ mkDir: dir === 'desc' ? 'asc' : null });
    else updateParams({ mkSort: k, mkDir: null });
  };
  const pickFilter = (k: MkFilter) => updateParams({ mkFilter: k === 'all' ? null : k });
  const reset = () => updateParams({ mkSort: null, mkDir: null, mkFilter: null });

  const filterTest: Record<MkFilter, (m: MkRow) => boolean> = {
    all: () => true,
    women: isWoman,
    men: isMan,
    ministers: m => !!m.governmentMember,
    norwegian: () => true,
    newMks: m => m.knessetCount === 1,
    veterans: m => (m.knessetCount || 0) >= 3,
  };
  const source = (k: MkFilter) => (k === 'norwegian' ? norwegian : members);
  const filterCount = (k: MkFilter) => source(k).filter(filterTest[k]).length;

  /* The list: filter, then sort by the metric, or by bloc, party and name */
  const byName = (a: MkRow, b: MkRow) => a.name.localeCompare(b.name, 'he');
  const sorted = source(filter).filter(filterTest[filter]).slice().sort((a, b) => {
    if (!sort) return ((a.isCoalition ? 0 : 1) - (b.isCoalition ? 0 : 1)) || a.partyName.localeCompare(b.partyName, 'he') || byName(a, b);
    const av = statValue(a, sort); const bv = statValue(b, sort);
    if (av === null && bv === null) return byName(a, b);
    if (av === null) return 1;
    if (bv === null) return -1;
    return (dir === 'desc' ? bv - av : av - bv) || byName(a, b);
  });
  let maxVal: number | null = null;
  if (sort) {
    for (const m of sorted) { const v = statValue(m, sort); if (v !== null && (maxVal === null || v > maxVal)) maxVal = v; }
    if (maxVal !== null && maxVal <= 0) maxVal = 1;
  }

  /* Knesset-wide figures on the tiles */
  const led = members.reduce((s, m) => s + (m.billStats ? m.billStats.led : 0), 0);
  const passed = members.reduce((s, m) => s + (m.billStats ? m.billStats.passed : 0), 0);
  const expenseTotals = members.map(m => m.expenseTotal || 0).filter(e => e > 0);
  const avgExpense = avgOf(expenseTotals);
  const attendance = avgOf(members.map(m => m.attendancePct).filter((a): a is number => typeof a === 'number'));
  const values: Record<MkMetric, React.ReactNode | null> = {
    age: avgOf(members.map(m => m.age).filter((a): a is number => typeof a === 'number')),
    seniority: avgOf(members.map(m => m.yearsInPolitics).filter((y): y is number => typeof y === 'number' && y > 0)),
    committees: members.reduce((s, m) => s + (m.committeeIds || []).length, 0).toLocaleString('he-IL'),
    bills: led ? led.toLocaleString('he-IL') : null,
    inProgress: members.reduce((s, m) => s + (m.billStats ? m.billStats.inProgress : 0), 0).toLocaleString('he-IL'),
    passed: passed ? passed.toLocaleString('he-IL') : null,
    passRate: led > 0 ? `${Math.round((passed / led) * 100)}%` : null,
    votes: attendance !== null ? `${attendance}%` : null,
    queries: members.reduce((s, m) => s + (m.queryCount || 0), 0).toLocaleString('he-IL'),
    agendaMotions: motionTotal !== null ? motionTotal.toLocaleString('he-IL') : null,
    expenses: avgExpense ? `₪${Math.round(avgExpense / 1000)}K` : null,
    lobbyists: lobbyistTotal,
  };

  const notes: Partial<Record<MkMetric, string>> = {
    votes: 'נוכחות בהצבעות המליאה בלבד. לנוכחות פיזית: zmanknesset.co.il',
    passRate: 'חוקים שעברו מתוך ההצעות שחבר/ת הכנסת בין שלושת היוזמים הראשונים שלהן; רק ח״כים עם 3 הצעות או יותר',
    bills: 'הצעות שחבר/ת הכנסת בין שלושת היוזמים הראשונים שלהן',
    agendaMotions: 'הצעות לסדר היום שחבר/ת הכנסת הגיש/ה, לבד או כחלק מהצעה כוללת',
    lobbyists: 'שדלנים שהופיעו בוועדות שחבר/ת הכנסת חבר/ה מלא/ה בהן. אין זה אומר שנפגשו.',
    seniority: 'שנים מאז הכניסה הראשונה לכנסת',
    committees: 'חברות מלאה בלבד, בלי מילוי מקום',
  };
  const filterNote = filter === 'norwegian' ? 'שרים שוויתרו על מושבם בכנסת לפי החוק הנורווגי' : filter === 'newMks' ? 'בכנסת הראשונה שלהם' : undefined;
  const note = (sort && notes[sort]) || filterNote;

  // Filters with nothing to show are left out
  const visibleFilters = MK_FILTERS.filter(x => x.key === 'all' || x.key === filter || filterCount(x.key) > 0);
  const noun = (MK_FILTERS.find(x => x.key === filter) || MK_FILTERS[0]).noun;
  const presentCount = members.concat(norwegian).filter(m => !!present[m.id]).length;
  const statusText = `${sorted.length} ${noun} · ${sort ? `לפי ${MK_METRIC_PHRASE[sort]}, ${dir === 'desc' ? 'מהגבוה לנמוך' : 'מהנמוך לגבוה'}` : 'לפי גוש וסיעה'}`;

  /* Without a sort: blocs with their parties, largest first */
  type Group = { key: string; label: string; swatch: string; members: MkRow[] };
  type Bloc = { key: string; label: string; textColor: string; groups: Group[] };
  const groupByParty = (list: MkRow[], prefix: string): Group[] => {
    const order: string[] = [];
    const byParty: Record<string, MkRow[]> = {};
    for (const m of list) {
      const p = m.partyName || 'ללא סיעה';
      if (!byParty[p]) { byParty[p] = []; order.push(p); }
      byParty[p].push(m);
    }
    return order.map(p => ({ key: `${prefix}${p}`, label: p, swatch: byParty[p][0].partyColor, members: byParty[p] }))
      .sort((x, y) => y.members.length - x.members.length);
  };
  const blocs: Bloc[] = sort ? [] : !members.some(m => m.isCoalition === true)
    // No coalition yet: the parties, without bloc headings
    ? [{ key: 'all', label: '', textColor: color.text, groups: groupByParty(sorted, 'a_') }].filter(b => b.groups.length > 0)
    : [
      { key: 'coalition', label: 'קואליציה', textColor: color.coalition, groups: groupByParty(sorted.filter(m => m.isCoalition === true), 'c_') },
      { key: 'opposition', label: 'אופוזיציה', textColor: color.oppositionText, groups: groupByParty(sorted.filter(m => m.isCoalition !== true), 'o_') },
    ].filter(b => b.groups.length > 0);

  const rows = (list: MkRow[]) => (isDesktop
    // Desktop: as many 340px+ columns as fit (2 at 1100px, 3 at 1440px), wide enough for the longest role tags
    ? <Grid onlyOnDesktop minColWidth={340} gap="10px">{list.map(m => <Card key={m.id} padding={0}><MkListRow member={m} sort={sort} maxVal={maxVal} filter={filter} showParty={!!sort} first inGrid present={!!present[m.id]} /></Card>)}</Grid>
    : <Card padding={0}>{list.map((m, i) => <MkListRow key={m.id} member={m} sort={sort} maxVal={maxVal} filter={filter} showParty={!!sort} first={i === 0} inGrid={false} present={!!present[m.id]} />)}</Card>);

  const groupBlock = (g: Group) => {
    const open = !collapsed[g.key];
    return (
      <div key={g.key} style={{ display: 'flex', flexDirection: 'column', gap: space.xs }}>
        <button
          type="button"
          aria-expanded={open}
          onClick={() => setCollapsed({ ...collapsed, [g.key]: open })}
          style={{ ...buttonReset, minHeight: TAP, display: 'flex', alignItems: 'center', gap: space.sm }}
        >
          <span style={{ width: 10, height: 10, borderRadius: 3, background: g.swatch || color.border, flexShrink: 0 }} />
          <span style={{ fontSize: font.label, fontWeight: weight.bold, color: color.text }}>{g.label}</span>
          <span style={{ fontSize: font.small, color: color.textSecondary }}>{g.members.length}</span>
          <span style={{ flex: 1 }} />
          <span style={{ color: color.textTertiary }}><Icon name={open ? 'chevronUp' : 'chevronDown'} size={18} /></span>
        </button>
        {open && rows(g.members)}
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: space.md }}>
      <MetricsCard
        title="מדדי הכנסת"
        groups={MK_METRIC_GROUPS}
        labels={MK_METRIC_LABEL}
        values={values}
        sort={sort}
        dir={dir}
        onSort={pickSort}
        filters={visibleFilters.map(x => ({ key: x.key, label: x.label, count: filterCount(x.key) }))}
        filter={filter}
        onFilter={pickFilter}
      />

      <div>
        <ResultBar
          action={(sort || filter !== 'all') ? <Button variant="ghost" onClick={reset} style={{ padding: `0 ${space.sm}px` }}>איפוס</Button> : undefined}
          style={{ margin: `-${space.xs}px 0` }}
        >
          {statusText}
        </ResultBar>
        {note && <div style={{ fontSize: font.small, color: color.textSecondary, lineHeight: 1.5 }}>{note}</div>}
        {presentCount > 0 && <PresenceLegend count={presentCount} onClick={() => updateParams({ tab: 'presence', mkSort: null, mkDir: null, mkFilter: null })} />}
      </div>

      {sort === 'expenses' && <ExpenseBreakdown totalSpending={expenseTotals.reduce((s, e) => s + e, 0)} />}

      {sorted.length === 0 && <Card padding={0}><EmptyState icon="users" title="אין תוצאות" /></Card>}

      {sorted.length > 0 && (blocs.length === 0
        ? rows(sorted)
        : blocs.map(b => (
          <section key={b.key} style={{ display: 'flex', flexDirection: 'column', gap: space.sm }}>
            {b.label && (
              <h2 style={{ ...text.heading, color: b.textColor, display: 'flex', alignItems: 'baseline', gap: space.sm }}>
                {b.label}
                <span style={{ fontSize: font.small, fontWeight: weight.regular, color: color.textSecondary }}>{b.groups.reduce((s, g) => s + g.members.length, 0)} {noun}</span>
              </h2>
            )}
            {b.groups.map(groupBlock)}
          </section>
        )))}
    </div>
  );
};

/* ── Page ────────────────────────────────────────────────────────────────── */

const PartiesPage = () => {
  const { isDesktop } = useBreakpoint();
  const [parties, setParties] = useState<Party[] | null>(null);
  const [allMembers, setAllMembers] = useState<MemberIndexEntry[] | null>(null);
  const [error, setError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const scope = useScope();
  const [searchParams, setSearchParams] = useUrlParams();
  const presence = usePresenceNow();

  // Tabs, ordering, sort and filters live in the URL, so Back returns to them
  const tabParam = searchParams.get('tab');
  const activeTab: ActiveTab = tabParam === 'members' || tabParam === 'presence' ? tabParam : 'parties';
  const orderingParam = searchParams.get('ordering');
  // The Knesset shown (?k=, an archived one); the presence tab is always the current Knesset
  const kParam = searchParams.get('k');
  const dataParam = tabParam === 'presence' ? null : kParam;
  const viewKnesset = knessetFromParam(dataParam, scope);
  const sortParam = searchParams.get('mkSort') as MkMetric | null;
  const mkSort: MkMetric | null = sortParam && MK_METRICS.indexOf(sortParam) >= 0 ? sortParam : null;
  const mkDir: 'asc' | 'desc' = searchParams.get('mkDir') === 'asc' ? 'asc' : 'desc';
  const filterParam = searchParams.get('mkFilter') as MkFilter | null;
  const mkFilter: MkFilter = filterParam && MK_FILTERS.some(x => x.key === filterParam) ? filterParam : 'all';

  const updateParams = useCallback((updates: Record<string, string | null>) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      Object.keys(updates).forEach(key => {
        const value = updates[key];
        if (value === null) next.delete(key); else next.set(key, value);
      });
      return next;
    });
  }, [setSearchParams]);

  // The factions and member index of the Knesset shown (cached and shared with the other
  // pages). Resolved from the loaded scope, so a first render's default never loads the wrong one.
  useEffect(() => {
    let cancelled = false;
    setError(false);
    setParties(null);
    setAllMembers(null);
    getScope().then(sc => {
      const k = knessetFromParam(dataParam, sc);
      getPartiesOf(k)
        .then(ps => { if (!cancelled) setParties(ps.map(p => ({ ...p, name: shortPartyName(p.name || '') }))); })
        .catch(() => { if (!cancelled) setError(true); });
      getAllMembersOf(k)
        .then(ms => { if (!cancelled) setAllMembers(ms); })
        .catch(() => { if (!cancelled) setError(true); });
    });
    return () => { cancelled = true; };
  }, [reloadKey, dataParam]);

  const { members, norwegian } = useMemo(() => {
    if (!parties || !allMembers) return { members: null as MkRow[] | null, norwegian: [] as MkRow[] };
    const byId: Record<string, Party> = {};
    parties.forEach(p => { byId[p.id] = p; });
    // Before a coalition exists nobody is coalition or opposition
    const blocs = hasCoalition(parties);
    const toRow = (m: MemberIndexEntry): MkRow => {
      const p = m.partyId ? byId[m.partyId] : undefined;
      return { ...m, age: m.birthYear ? new Date().getFullYear() - m.birthYear : undefined, partyName: p ? p.name : '', partyColor: p ? p.color : color.border, isCoalition: p && blocs ? !!p.isCoalition : undefined };
    };
    return {
      // Sitting MKs; Norwegian Law ministers join only through the ministers tile
      members: allMembers.filter(m => m.isCurrentMk === true).map(toRow),
      norwegian: allMembers.filter(m => m.governmentMember && m.isActiveMk === false).map(toRow),
    };
  }, [parties, allMembers]);

  // Scroll restoration per tab: save on unmount, restore once the tab's data has loaded
  const restored = useRef(false);
  const ready = activeTab === 'parties' ? !!parties : !!members;
  useEffect(() => {
    if (restored.current || !ready) return;
    restored.current = true;
    try {
      const key = `parties_scroll_${activeTab}`;
      const saved = sessionStorage.getItem(key);
      if (saved) { window.scrollTo(0, parseInt(saved, 10) || 0); sessionStorage.removeItem(key); }
    } catch { /* ok */ }
  }, [ready, activeTab]);
  useEffect(() => {
    const key = `parties_scroll_${activeTab}`;
    return () => { try { sessionStorage.setItem(key, String(window.scrollY)); } catch { /* ok */ } };
  }, [activeTab]);

  const seated = (parties || []).filter(p => p.seats > 0);
  // Before a coalition exists the factions are grouped by camp, and there's no coalition view
  const noCoalition = !!parties && seated.length > 0 && !hasCoalition(seated);
  const defaultOrdering: Ordering = noCoalition ? 'wing' : 'coalition';
  const ordering: Ordering = orderingParam === 'seats' || orderingParam === 'wing' || (orderingParam === 'coalition' && !noCoalition) ? orderingParam : defaultOrdering;
  const coalition = seated.filter(p => p.isCoalition).sort((a, b) => b.seats - a.seats);
  const opposition = seated.filter(p => !p.isCoalition).sort((a, b) => b.seats - a.seats);
  const sum = (ps: Party[]) => ps.reduce((s, p) => s + p.seats, 0);

  const wingGroups = (() => {
    const groups: Record<string, Party[]> = {};
    seated.forEach(p => {
      // A faction not classified yet (e.g. a new Knesset's) still gets a group
      const wing = p.politicalWing || UNCLASSIFIED;
      if (!groups[wing]) groups[wing] = [];
      groups[wing].push(p);
    });
    const wings = WING_ORDER.filter(w => groups[w]).concat(Object.keys(groups).filter(w => WING_ORDER.indexOf(w) < 0));
    return wings.map(w => ({ wing: w, parties: groups[w].sort((a, b) => b.seats - a.seats) }));
  })();

  const memberCount = members ? members.length : null;
  // Dots and the tab count only while the presence list is fresh (polled in the last 10 minutes),
  // and no "0" on the tab: nights, weekends and recesses would show it most of the time
  const presentNow: Record<string, string> = presence.now && !isPresenceStale(presence.now, presence.clock) ? presence.now.present : {};
  const presentCount = Object.keys(presentNow).length || undefined;
  const subtitle = parties
    ? `הכנסת ה-${viewKnesset} · ${seated.length} סיעות · ${memberCount != null ? memberCount : 120} חברי כנסת${noCoalition ? ' · טרם הוקמה קואליציה' : ''}`
    : `הכנסת ה-${viewKnesset}`;

  let body: React.ReactNode;
  if (error && !(parties && members)) {
    body = <Card padding={0}><ErrorState onRetry={() => setReloadKey(k => k + 1)} /></Card>;
  } else if (activeTab === 'parties') {
    body = !parties
      ? (
        <div data-skeleton="true" style={{ display: 'flex', flexDirection: 'column', gap: space.lg }}>
          <Card><SkeletonText lines={3} /></Card>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: space.sm }}>
            {Array.from({ length: 6 }, (_, i) => <Skeleton key={i} height={150} rounded={radius.xl} />)}
          </div>
        </div>
      )
      : (
        // Desktop: the compare card moves to a side column; phone and tablet keep it inline
        <TwoPane side={<CompareCard parties={parties} members={members} />} sidePosition="end" sideWidth={340} stickySide={false} sideWhenStacked="hidden">
          <div style={{ display: 'flex', flexDirection: 'column', gap: space.lg }}>
            {noCoalition ? <CampBalance groups={wingGroups} /> : <PowerBalance coalition={coalition} opposition={opposition} />}
            {!isDesktop && <CompareCard parties={parties} members={members} />}

            {/* Three views of the same parties: a segmented control, all options in view */}
            <div>
              <div style={{ fontSize: font.small, color: color.textSecondary, marginBottom: space.xs }}>סידור הסיעות</div>
              <TabBar
                label="סידור הסיעות"
                tabs={noCoalition ? ORDERING_TABS_NO_COALITION : ORDERING_TABS}
                active={ordering}
                onChange={o => updateParams({ ordering: o === defaultOrdering ? null : o })}
                style={isDesktop ? { maxWidth: 420 } : undefined}
              />
            </div>

            {ordering === 'coalition' && (
              <>
                <PartyBlock title="קואליציה" seats={sum(coalition)} swatch={color.coalition} parties={coalition} />
                <PartyBlock title="אופוזיציה" seats={sum(opposition)} swatch={color.opposition} parties={opposition} />
              </>
            )}
            {ordering === 'seats' && <PartyGrid parties={seated.slice().sort((a, b) => b.seats - a.seats)} />}
            {ordering === 'wing' && wingGroups.map(g => (
              <PartyBlock key={g.wing} title={g.wing} seats={sum(g.parties)} swatch={WING_COLORS[g.wing] || color.textTertiary} parties={g.parties} />
            ))}
          </div>
        </TwoPane>
      );
  } else if (activeTab === 'presence') {
    body = !members
      ? (
        <div data-skeleton="true" style={{ display: 'flex', flexDirection: 'column', gap: space.lg }}>
          <SkeletonTiles count={3} /><SkeletonList rows={8} avatar />
        </div>
      )
      // Sitting MKs and Norwegian Law ministers: everyone the presence board lists
      : <PresenceTab people={members.concat(norwegian.filter(n => !members.some(m => m.id === n.id)))} presence={presence} searchParams={searchParams} updateParams={updateParams} />;
  } else {
    body = !members
      ? (
        <div data-skeleton="true" style={{ display: 'flex', flexDirection: 'column', gap: space.lg }}>
          <SkeletonTiles count={4} /><SkeletonTiles count={4} /><SkeletonList rows={8} avatar />
        </div>
      )
      : <MembersTab members={members} norwegian={norwegian} sort={mkSort} dir={mkDir} filter={mkFilter} updateParams={updateParams} present={presentNow} knesset={viewKnesset} />;
  }

  return (
    <Layout width="full">
      <PageContainer>
        <div style={{ padding: contentPadding(isDesktop), display: 'flex', flexDirection: 'column', gap: space.lg }}>
          <PageHeader
            title="הכנסת"
            subtitle={subtitle}
            action={activeTab !== 'presence' ? <KnessetMenu value={viewKnesset} onChange={k => updateParams({ k: k === scope.knesset ? null : String(k) })} /> : undefined}
          />
          <TabBar
            label="תצוגה"
            tabs={[
              { key: 'parties', label: 'סיעות', count: parties ? seated.length : undefined },
              { key: 'members', label: 'חברי כנסת', count: memberCount != null ? memberCount : undefined },
              { key: 'presence', label: 'במשכן', count: presentCount },
            ]}
            active={activeTab}
            onChange={t => updateParams({ tab: t === 'parties' ? null : t })}
            style={isDesktop ? { maxWidth: 480 } : undefined}
          />
          {body}
        </div>
      </PageContainer>
    </Layout>
  );
};

export default PartiesPage;
